import React, { useState } from 'react';
import { Check, Copy, Phone, Voicemail } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { Button } from '../ui/Button';
import { NumberedSteps } from '../activation/ActivationPrimitives';
import type { ForwardingConnection } from '../../types/forwarding';
import { getCarrier } from '../../data/mobileForwarding';
interface RemoveAllForwardingStepProps {
  connection: ForwardingConnection;
  onConfirmed: () => void;
  onCancel: () => void;
}

/**
 * Step 1 of switching from "Answer all calls" to "Only when I can't answer":
 * guide the user to remove all-call forwarding, then let them confirm it.
 */
export function RemoveAllForwardingStep({
  connection,
  onConfirmed,
  onCancel
}: RemoveAllForwardingStepProps) {
  const {
    isLight
  } = useTheme();
  const [confirmed, setConfirmed] = useState(false);
  const [copied, setCopied] = useState(false);
  const setup = connection.setup;
  const carrier = getCarrier(setup?.carrierId ?? 'other');
  const code = carrier.codes.all?.deactivate;
  const viaSettings = setup?.method === 'settings' || !code;
  const os = setup?.os ?? 'ios';
  const B = ({
    children
  }: {
    children: React.ReactNode;
  }) => <span className={`font-semibold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-630" data-mp="2gs3i5fw97nfn">{children}</span>;
  const settingsSteps = os === 'ios' ? [<>Open iPhone <B data-id="element-631" data-mp="2lok7gdf6w7ub">Settings</B>.</>, <>Go to <B data-id="element-632" data-mp="2qqt3jinczq0n">Apps → Phone → Call Forwarding</B>.</>, <>If you have two lines, choose the one connected to Vexu.</>, <>Turn <B data-id="element-633" data-mp="2vnjh9zboy9iw">Call Forwarding</B> off, then come back here.</>] : [<>Open the <B data-id="element-634" data-mp="30h46b1vb3t5y">Phone</B> app, tap the <B data-id="element-635" data-mp="30hxhqg57n6uf">three-dot menu</B>, then <B data-id="element-636" data-mp="34h6bhl0934pz">Settings</B>.</>, <>
            Open <B data-id="element-637" data-mp="34gwkbu3gyrip">{setup?.androidBrand === 'samsung' ? 'Supplementary services' : 'Calls'}</B>, then{' '}
            <B data-id="element-638" data-mp="39i2frzoiksvx">Call forwarding</B>.
          </>, <>If asked, pick the SIM connected to Vexu, then <B data-id="element-639" data-mp="3eg3uy9itk824">Voice</B>.</>, <>Tap <B data-id="element-640" data-mp="3ed99qfrzej7n">Always forward</B>, choose <B data-id="element-641" data-mp="1ect9whevbj1g">Turn off</B>, then come back here.</>];
  const copy = async () => {
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
    } catch {
      /* clipboard unavailable — the code stays visible */
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1600);
  };
  const muted = isLight ? 'text-slate-500' : 'text-zinc-400';
  return <div data-id="element-642" data-mp="1x3ayqawkxuhj">
      <p className={`text-xs font-bold ${isLight ? 'text-vexu-700' : 'text-vexu'}`} data-id="element-643" data-mp="1x32kaekt6h48">Step 1 of 2</p>
      <p className={`mt-1 text-[15px] font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-644" data-mp="1t30gnzvijdmb">
        First, turn off answering all calls
      </p>
      <p className={`mt-1 mb-4 text-sm leading-relaxed ${muted}`} data-id="element-645" data-mp="26zxh8t1fo9ge">
        While it's on, every call keeps going straight to Vexu. Remove it{' '}
        {viaSettings ? 'from your phone settings' : 'with this code'}, then choose when Vexu should answer.
      </p>

      {viaSettings ? <NumberedSteps steps={settingsSteps} data-id="element-646" data-mp="7qqoam2bv1h5" /> : <div className={`mb-5 rounded-2xl border p-4 ${isLight ? 'border-slate-200 bg-white shadow-sm' : 'border-white/10 bg-white/5'}`} data-id="element-647" data-mp="2jbzm0jjiq44v">
          <p className={`text-xs ${muted}`} data-id="element-648" data-mp="2od6s8e60iv9f">
            Dial this code
          </p>
          <div className="mt-1.5 flex items-center justify-between gap-3" data-id="element-649" data-mp="29kdeoyaawp7e">
            <code className={`text-2xl font-bold tracking-wider ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-650" data-mp="2ek822ruvawnp">{code}</code>
            <button type="button" onClick={copy} aria-label="Copy code" className={`flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors ${isLight ? 'border-slate-200 text-slate-600 hover:bg-slate-50' : 'border-white/10 text-zinc-300 hover:bg-white/5'}`} data-id="element-651" data-mp="20nuohttgffei">
              {copied ? <Check className="h-3.5 w-3.5 text-vexu" data-id="element-652" data-mp="3l24ndxhocv74" /> : <Copy className="h-3.5 w-3.5" data-id="element-653" data-mp="3q0fyscnmbdrg" />}
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
          <a href={`tel:${encodeURIComponent(code as string)}`} className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl bg-vexu py-2.5 text-sm font-bold text-white transition-colors hover:bg-vexu-600" data-id="element-654" data-mp="1pyq7w922k5o4">
            <Phone className="h-4 w-4" data-id="element-655" data-mp="1gzl8l0ioo8aw" />
            Dial code
          </a>
          <p className={`mt-2.5 text-xs leading-relaxed ${muted}`} data-id="element-656" data-mp="1by5r1ifpsjx9">
            Your carrier should show a message like “Call forwarding disabled”.
            {carrier.codes.all?.deactivateIsBlanket && ' This removes all forwarding on your line.'}
          </p>
        </div>}

      <p className={`mb-4 flex gap-2 text-xs leading-relaxed ${muted}`} data-id="element-657" data-mp="y82933nvbcdm">
        <Voicemail className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" data-id="element-658" data-mp="t8pj4a1p9p6t" />
        If you used voicemail before Vexu, you may need to turn it back on.
      </p>

      <div role="checkbox" aria-checked={confirmed} tabIndex={0} onClick={() => setConfirmed(v => !v)} onKeyDown={e => {
      if (e.key === ' ' || e.key === 'Enter') {
        e.preventDefault();
        setConfirmed(v => !v);
      }
    }} className={`flex cursor-pointer items-start gap-3 rounded-2xl border p-3.5 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-vexu ${confirmed ? 'border-vexu bg-vexu/10' : isLight ? 'border-slate-200 hover:border-slate-300' : 'border-white/10 hover:border-white/20'}`} data-id="element-659" data-mp="ob79ehfuz5xr">
        <span className={`mt-0.5 grid h-5 w-5 flex-shrink-0 place-items-center rounded-md border-2 ${confirmed ? 'border-vexu bg-vexu text-white' : isLight ? 'border-slate-300 bg-white' : 'border-white/30'}`} data-id="element-660" data-mp="3rjswifow5qi2">
          {confirmed && <Check className="h-3.5 w-3.5" strokeWidth={3} data-id="element-661" data-mp="559hcgs3e7aj" />}
        </span>
        <span className={`text-[13px] leading-relaxed ${isLight ? 'text-slate-700' : 'text-zinc-300'}`} data-id="element-662" data-mp="33v9o805uddpz">
          I've turned off forwarding for all calls
        </span>
      </div>

      <div className="mt-5 space-y-2" data-id="element-663" data-mp="30slpgi6f8x8q">
        <Button fullWidth disabled={!confirmed} onClick={onConfirmed} data-id="element-664" data-mp="30rkaw3q75sm9">
          Next: choose when Vexu answers
        </Button>
        <button type="button" onClick={onCancel} className={`w-full py-2 text-sm font-medium ${isLight ? 'text-slate-500 hover:text-slate-700' : 'text-zinc-400 hover:text-zinc-200'}`} data-id="element-665" data-mp="2vrpk006f9317">
          Keep answering all calls
        </button>
      </div>
    </div>;
}